// ABOUTME: Top navigation bar with panel toggle icons that show/hide based on panel state
// ABOUTME: Includes surface menu centered on surface area and panel icons that disappear when panels open

import React, { useContext, useCallback, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { BookOpen, Inbox, NotebookText, Settings } from 'lucide-react';
import { debounce } from 'lodash';
import { usePanelContext } from 'src/context/PanelContext';
import { Z_INDEX } from 'src/constants/panelSizes';
import { SearchContext } from 'src/context/SearchContext';
import { ShownIdsContext } from 'src/context/ShownContext';
import SurfaceDropdown from 'src/components/Panels/SurfaceDropdown';
import SearchBar from 'src/components/SearchBar';
import { PanelToggleButton } from './PanelToggleButton';

const NavBarContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: ${Z_INDEX.NAV_BAR};
`;

const LeftIconGroup = styled.div`
  position: absolute;
  left: 1.5rem;
  top: 1.5rem;
  display: flex;
  gap: 0.5rem;
  align-items: center;
  pointer-events: auto;
`;

const SearchBarWrapper = styled.div`
  width: 270px;
  margin-right: 4px;
  transition: width 250ms ease-in-out;
`;

const ReaderIconWrapper = styled.div`
  display: flex;
  align-items: center;
  pointer-events: auto;
  margin-left: 1rem;
`;

const CenterGroup = styled.div<{ $offset: number; $minLeft: number }>`
  position: absolute;
  left: ${({ $offset, $minLeft }) => `max(${$minLeft}px, ${$offset}px)`};
  transform: translateX(-50%);
  top: 1.5rem;
  display: flex;
  gap: 0.5rem;
  align-items: center;
  pointer-events: auto;
  transition: left 250ms ease-in-out;
`;

const RightIconGroup = styled.div`
  position: absolute;
  right: 1.5rem;
  top: 1.5rem;
  display: flex;
  gap: 1rem;
  pointer-events: auto;
`;

const IconWrapper = styled.div<{ $visible: boolean }>`
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  transition: opacity 250ms ease-in-out;
`;

export const TopNavBar: React.FC = () => {
  const { panels, togglePanel, togglePinned, openPanel, closePanel, leftPanelsWidth, rightPanelsWidth } =
    usePanelContext();
  const { searchTerm, setSearchTerm } = useContext(SearchContext);
  const { shownIds } = useContext(ShownIdsContext);

  // Use refs to track latest state values for the blur timeout callback.
  // This prevents stale closure issues where a scheduled timeout captures old values.
  const stateRef = useRef({
    libIsOpen: panels.lib.isOpen,
    libIsPinned: panels.lib.isPinned,
    searchTermLength: searchTerm.length,
    shownIdsLength: shownIds.length,
  });

  // Keep refs in sync with latest state
  useEffect(() => {
    stateRef.current = {
      libIsOpen: panels.lib.isOpen,
      libIsPinned: panels.lib.isPinned,
      searchTermLength: searchTerm.length,
      shownIdsLength: shownIds.length,
    };
  }, [panels.lib.isOpen, panels.lib.isPinned, searchTerm.length, shownIds.length]);

  const handleLibraryIconClick = (): void => {
    if (panels.lib.isPinned) {
      // Library is pinned: unpin, clear search, and close
      setSearchTerm('');
      togglePinned('lib'); // This will unpin
      closePanel('lib');
    } else {
      // Library is not pinned: pin it (this also opens it if not open)
      if (!panels.lib.isOpen) {
        openPanel('lib');
      }
      togglePinned('lib'); // This will pin
    }
  };

  // Update the search term in context with debounce
  const onSearchValueChange = debounce((value: string): void => {
    setSearchTerm(value);
    // Auto-open library when user types in search (if not already open)
    if (value.length > 0 && !panels.lib.isOpen) {
      openPanel('lib');
    }
  }, 300);

  const handleSearchFocus = (): void => {
    // Auto-open library when search gets focus (if not already open)
    if (!panels.lib.isOpen) {
      openPanel('lib');
    }
  };

  // Auto-close library when search loses focus AND search is empty AND no selected cards.
  // Uses refs to read latest state values when timeout fires, avoiding stale closure issues.
  const handleSearchBlur = useCallback((): void => {
    // Use setTimeout to allow click events on library to fire first
    setTimeout(() => {
      // Check if focus is still on the search bar (user clicked back into it)
      const { activeElement } = document;
      const isSearchFocused = activeElement?.id === 'searchbar';

      // Read latest state from refs to avoid stale closure values
      const { libIsOpen, libIsPinned, searchTermLength, shownIdsLength } = stateRef.current;

      // Only auto-close if library is open, not pinned, search is empty, no selected cards,
      // AND the search bar doesn't have focus
      if (libIsOpen && !libIsPinned && searchTermLength === 0 && shownIdsLength === 0 && !isSearchFocused) {
        closePanel('lib');
      }
    }, 200);
  }, [closePanel]);

  // Calculate center of surface area (between left and right panels)
  const surfaceCenterX = leftPanelsWidth + (window.innerWidth - leftPanelsWidth - rightPanelsWidth) / 2;

  // Search bar should be subtle when library is closed and no search term
  const isSearchSubtle = !panels.lib.isOpen && searchTerm.length === 0;

  return (
    <NavBarContainer>
      <LeftIconGroup>
        <SearchBarWrapper>
          <SearchBar
            onSearch={onSearchValueChange}
            onFocus={handleSearchFocus}
            onBlur={handleSearchBlur}
            isSubtle={isSearchSubtle}
          />
        </SearchBarWrapper>
        <PanelToggleButton
          icon={Inbox}
          onClick={handleLibraryIconClick}
          title={panels.lib.isPinned ? 'Unpin Library' : 'Pin Library'}
          isActive={panels.lib.isPinned || false}
        />
        <ReaderIconWrapper>
          <IconWrapper $visible={!panels.reader.isOpen}>
            <PanelToggleButton
              icon={BookOpen}
              onClick={() => togglePanel('reader')}
              title="Reader"
              isActive={panels.reader.isPinned || panels.reader.isOpen}
            />
          </IconWrapper>
        </ReaderIconWrapper>
      </LeftIconGroup>

      <CenterGroup $offset={surfaceCenterX} $minLeft={420}>
        <SurfaceDropdown />
      </CenterGroup>

      <RightIconGroup>
        <IconWrapper $visible={!panels.writer.isOpen}>
          <PanelToggleButton
            icon={NotebookText}
            onClick={() => togglePanel('writer')}
            title="Writer"
            isActive={panels.writer.isPinned || panels.writer.isOpen}
          />
        </IconWrapper>
        <IconWrapper $visible={!panels.profile.isOpen}>
          <PanelToggleButton
            icon={Settings}
            onClick={() => togglePanel('profile')}
            title="Settings"
            isActive={panels.profile.isPinned || panels.profile.isOpen}
          />
        </IconWrapper>
      </RightIconGroup>
    </NavBarContainer>
  );
};
